import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { hexToRGBA } from '../BarChart';

interface BreadCrumb {
  title: string;
  link: string;
}

interface BreadCrumbProps {
  links: BreadCrumb[];
}

export default function BreadCrumb(props: BreadCrumbProps) {
  const { links } = props;
  const theme = useTheme();

  const Separator = () => (
    <ChevronRightRoundedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
  );

  return (
    <Box
      role="presentation"
      sx={{
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Breadcrumbs
        aria-label="breadcrumb"
        separator={<Separator />}
        sx={{
          '& .MuiBreadcrumbs-ol': {
            flexWrap: 'nowrap',
            gap: 0.5,
          },
        }}
      >
        {links.map((breadCrumb, index) => {
          const isLast = index === links.length - 1;
          const isFirst = index === 0;
          const maxWidth = { xs: 200, sm: 300, md: 400 };

          const content = (
            <Tooltip title={breadCrumb.title} disableInteractive>
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  maxWidth,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  verticalAlign: 'middle',
                }}
              >
                {breadCrumb.title}
              </Box>
            </Tooltip>
          );

          // First element (Home icon)
          if (isFirst && !isLast) {
            return (
              <Link
                underline="none"
                component={RouterLink}
                to={breadCrumb.link}
                key={`home-${breadCrumb.title}`}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 0.75,
                  borderRadius: '8px',
                  color: 'text.secondary',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    color: 'primary.main',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                <Tooltip
                  title={breadCrumb.title || 'Inicio'}
                  disableInteractive
                >
                  <HomeRoundedIcon
                    sx={{
                      fontSize: 20,
                      display: 'block',
                    }}
                  />
                </Tooltip>
              </Link>
            );
          }

          // Last element (Current/Active)
          if (isLast) {
            const primaryColor = theme.palette.primary.main;
            return (
              <Typography
                key={breadCrumb.title}
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '14px', sm: '15px', md: '16px' },
                  display: 'inline-flex',
                  alignItems: 'center',
                  backgroundColor: hexToRGBA(primaryColor, 0.12),
                  color: primaryColor,
                  borderRadius: '8px',
                  px: 1.5,
                  py: 0.75,
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {content}
              </Typography>
            );
          }

          // Intermediate elements
          return (
            <Link
              underline="none"
              component={RouterLink}
              to={breadCrumb.link}
              key={breadCrumb.title}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                fontWeight: 400,
                fontSize: { xs: '14px', sm: '15px', md: '16px' },
                color: 'text.secondary',
                px: 1,
                py: 0.5,
                borderRadius: '6px',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: 'action.hover',
                  fontWeight: 500,
                },
              }}
            >
              {content}
            </Link>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
}
